import React, { useState, useEffect } from 'react';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from './ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_nakasero-homes/artifacts/z9psxsca_5ca02298-928c-4bb4-98fb-11237ebedacb.jpeg";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', href: '#home' },
    { name: 'Apartments', href: '#apartments' },
    { name: 'Gallery', href: '#gallery' },
    { name: 'Amenities', href: '#amenities' },
    { name: 'Location', href: '#location' },
    { name: 'Contact', href: '#contact' }
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-white shadow-lg shadow-slate-900/5'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="#home" className="flex items-center gap-3 group">
            <img 
              src={LOGO_URL} 
              alt="Concord Apartments Logo" 
              className="w-12 h-12 rounded-xl object-cover shadow-md"
            />
            <div className="hidden sm:block">
              <span className={`font-serif text-xl font-semibold tracking-wide transition-colors duration-300 ${
                isScrolled ? 'text-slate-900' : 'text-white'
              }`}>
                Concord
              </span>
              <p className={`text-[10px] tracking-[0.15em] uppercase transition-colors duration-300 ${
                isScrolled ? 'text-slate-500' : 'text-white/60'
              }`}>Nakasero Hill</p>
            </div>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <button
                key={link.name}
                onClick={() => scrollToSection(link.href)}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-300 ${
                  isScrolled 
                    ? 'text-slate-600 hover:text-slate-900 hover:bg-slate-100' 
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
              >
                {link.name}
              </button>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <Button
              onClick={() => scrollToSection('#contact')}
              className={`rounded-full px-6 py-2 font-medium transition-all duration-300 ${
                isScrolled
                  ? 'bg-amber-500 hover:bg-amber-600 text-white'
                  : 'bg-white/10 hover:bg-white/20 text-white border border-white/20 backdrop-blur-sm'
              }`}
            >
              <Phone className="w-4 h-4 mr-2" />
              Schedule Visit
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className={`lg:hidden p-2 rounded-xl transition-colors ${
              isScrolled ? 'hover:bg-slate-100' : 'hover:bg-white/10'
            }`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className={`w-6 h-6 ${isScrolled ? 'text-slate-900' : 'text-white'}`} />
            ) : (
              <Menu className={`w-6 h-6 ${isScrolled ? 'text-slate-900' : 'text-white'}`} />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white rounded-2xl shadow-xl p-6 mt-2 border border-slate-100">
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100">
              <img 
                src={LOGO_URL} 
                alt="Concord Apartments Logo" 
                className="w-10 h-10 rounded-lg object-cover"
              />
              <div>
                <span className="font-serif text-lg font-semibold text-slate-900">Concord</span>
                <p className="text-[10px] text-slate-500 tracking-wider uppercase">Nakasero Hill</p>
              </div>
            </div>
            <nav className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <button
                  key={link.name}
                  onClick={() => scrollToSection(link.href)}
                  className="text-slate-700 hover:text-amber-600 hover:bg-amber-50 text-left py-3 px-4 rounded-xl transition-colors font-medium"
                >
                  {link.name}
                </button>
              ))}
              <div className="pt-4 mt-2 border-t border-slate-100">
                <Button
                  onClick={() => scrollToSection('#contact')}
                  className="bg-amber-500 hover:bg-amber-600 text-white w-full rounded-xl py-3"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Schedule Visit
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
